import { createContext, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  BASE_URL_SANDBOX,
  GET_SINGLE_CATEGORY_SEARCH_DATA,
} from '../Constants/Config';
import Cookies from 'js-cookie';
import { defaultTracyLatLng, isAgeConfirmationDisabled } from '../utils';
import { createRequestCache } from '../utils/requestCache';

const categoryRequestCache = createRequestCache({
  ttlMs: 30 * 1000,
  maxEntries: 150,
});

export const ContextSearch = createContext();

const SearchContext = ({ children }) => {
  const [noDataFound, setNoDataFound] = useState(false);
  const [ageConfirmation, setAgeConfirmation] = useState(false);
  const disableAgeConfirmation = useMemo(() => isAgeConfirmationDisabled(), []);

  const [rawShowAgeConfirmationModal, setRawShowAgeConfirmationModal] = useState(
    () => {
      if (disableAgeConfirmation) return false;
      const ageConfirmed = Cookies.get('ageConfirmed');
      return !Boolean(ageConfirmed);
    },
  );

  const showAgeConfirmationModal = disableAgeConfirmation
    ? false
    : rawShowAgeConfirmationModal;

  // Guard so callers can't force-enable the modal when disabled.
  const setShowAgeConfirmationModal = (next) => {
    setRawShowAgeConfirmationModal((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      return disableAgeConfirmation ? false : Boolean(resolved);
    });
  };
  const [loading, setLoading] = useState(false); // Add loading state
  const activeFetchCountRef = useRef(0);

  const fetchCategoryProducts = async (
    category,
    {
      page = 1,
      limit = 12,
      signal,
      forceRefresh = false,
    } = {},
  ) => {
    activeFetchCountRef.current += 1;
    try {
      setLoading(true);
      const searchParams = new URLSearchParams(window.location.search);
      const storeType = searchParams.get('store');
      const normalizedStoreType =
        storeType === 'delivery' || storeType === 'pickup' ? storeType : '';
      const storedLocation = Cookies.get('userLocation');
      let loc = defaultTracyLatLng;
      try {
        loc = storedLocation
          ? JSON.parse(decodeURIComponent(storedLocation))
          : defaultTracyLatLng;
      } catch {
        loc = defaultTracyLatLng;
      }

      const requestCategory = category ?? '';
      const requestPage = page ?? 1;
      const requestLimit = limit ?? 12;
      const requestLat = loc?.lat ?? '';
      const requestLong = loc?.lng ?? '';
      const requestKey = JSON.stringify([
        BASE_URL_SANDBOX,
        GET_SINGLE_CATEGORY_SEARCH_DATA,
        String(requestCategory),
        String(requestPage),
        String(requestLimit),
        String(requestLat),
        String(requestLong),
        normalizedStoreType,
      ]);

      const responseData = await categoryRequestCache.getOrCreate(
        requestKey,
        async () => {
          const formData = new FormData();
          formData.append('smartsearch', requestCategory);
          formData.append('page', requestPage);
          formData.append('limit', requestLimit);
          formData.append('lat', requestLat);
          formData.append('long', requestLong);
          if (normalizedStoreType) {
            formData.append('store', normalizedStoreType);
          }

          const response = await axios.post(
            BASE_URL_SANDBOX + GET_SINGLE_CATEGORY_SEARCH_DATA,
            formData,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
            },
          );

          return response?.data?.message;
        },
        { forceRefresh, signal },
      );
      
      if (!responseData?.store_products?.flat().length) {
        // ToastifyAlert('Oops! Looks like there are no products in this category yet', 'error');
        setNoDataFound(true);
      } else {
        setNoDataFound(false);
      }

      return responseData;
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        return null;
      }
      // ToastifyAlert('Failed to fetch products. Please try again.', 'error');
      setNoDataFound(true);
      return null;
    } finally {
      activeFetchCountRef.current = Math.max(
        0,
        activeFetchCountRef.current - 1,
      );
      if (activeFetchCountRef.current === 0) {
        setLoading(false);
      }
    }
  };

  const checkOrderMethod = (item) => {
    const isPickup = item?.is_pickup === 'Yes';
    const isDelivery = item?.is_deliver === 'Yes';
    return !isPickup && isDelivery ? 'delivery' : 'pickup';
  };
  return (
    <ContextSearch.Provider
      value={{
        noDataFound,
        setNoDataFound,
        loading, // Expose loading state
        fetchCategoryProducts,
        checkOrderMethod,
        ageConfirmation,
        setAgeConfirmation,
        disableAgeConfirmation,
        showAgeConfirmationModal,
        setShowAgeConfirmationModal,
      }}
    >
      {children}
    </ContextSearch.Provider>
  );
};

export default SearchContext;
